/**
 * Error types and interfaces for global error handling
 */

export interface CustomErrorInfo {
  componentStack?: string;
  errorBoundary?: string;
  errorBoundaryStack?: string;
}

export interface ErrorContext {
  userId?: string;
  sessionId?: string;
  route?: string;
  userAgent?: string;
  timestamp?: string;
  buildVersion?: string;
  environment?: string;
  errorBoundary?: string;
  level?: string;
  action?: string;
}

export interface ErrorDetails {
  id: string;
  message: string;
  stack?: string;
  name: string;
  cause?: Error;
  context?: ErrorContext;
  errorInfo?: CustomErrorInfo;
  severity: ErrorSeverity;
  category: ErrorCategory;
  recoverable: boolean;
  retryCount?: number;
  maxRetries?: number;
}

export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export enum ErrorCategory {
  NETWORK = 'network',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  VALIDATION = 'validation',
  BUSINESS_LOGIC = 'business_logic',
  SYSTEM = 'system',
  UI_COMPONENT = 'ui_component',
  UNKNOWN = 'unknown'
}

export interface ErrorRecoveryAction {
  id: string;
  label: string;
  action: () => void | Promise<void>;
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  disabled?: boolean;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorDetails: ErrorDetails | null;
  errorId: string | null;
  retryCount: number;
  isRetrying: boolean;
  showDetails: boolean;
}

export interface ErrorLogEntry {
  id: string;
  timestamp: string;
  error: ErrorDetails;
  userAgent: string;
  url: string;
  userId?: string;
  sessionId?: string;
}

export interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
  onError?: (error: Error, errorInfo: React.ErrorInfo, errorDetails: ErrorDetails) => void;
  enableLogging?: boolean;
  maxRetries?: number;
  isolate?: boolean;
  level?: 'page' | 'component' | 'global';
  resetKeys?: Array<string | number>;
  resetOnPropsChange?: boolean;
}

export interface ErrorFallbackProps {
  error: Error;
  errorDetails: ErrorDetails;
  resetError: () => void;
  retryAction: () => void;
  isRetrying: boolean;
  canRetry: boolean;
  recoveryActions?: ErrorRecoveryAction[];
}

export type ErrorHandler = (error: Error, errorInfo?: React.ErrorInfo) => void;

export interface GlobalErrorContextType {
  errors: ErrorDetails[];
  reportError: (error: Error, context?: Partial<ErrorContext>) => string;
  clearError: (errorId: string) => void;
  clearAllErrors: () => void;
  retryError: (errorId: string) => Promise<boolean>;
  getErrorsByCategory: (category: ErrorCategory) => ErrorDetails[];
  getErrorsBySeverity: (severity: ErrorSeverity) => ErrorDetails[];
}